import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, ArrowLeft, Loader2, Calendar, Check, Copy } from 'lucide-react';
import bridgeApi from './bridgeApi';

/**
 * ScheduleBuilder — Bulk game entry per ADMIN_UI_BLUEPRINT.md
 * 
 * Modes:
 * 1. Manual rows — add games one-by-one with inline editing
 * 2. Recurring pattern — e.g. "Every Tuesday at 7pm for 10 weeks"
 * 3. Copy prior season — duplicate games from an existing schedule
 */
export default function ScheduleBuilder({ orgId, schedules, teams, venues, onBack, onComplete }) {
  const [scheduleSlug, setScheduleSlug] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(0);
  const [errors, setErrors] = useState([]);
  const [mode, setMode] = useState('manual'); // manual | recurring | copy

  // Recurring pattern state
  const [recurDay, setRecurDay] = useState('2'); // 0=Sun..6=Sat
  const [recurTime, setRecurTime] = useState('19:00');
  const [recurStartDate, setRecurStartDate] = useState('');
  const [recurWeeks, setRecurWeeks] = useState(10);
  const [recurHome, setRecurHome] = useState(true);

  // Copy prior state
  const [copyFromSlug, setCopyFromSlug] = useState('');

  function emptyRow() {
    return { id: Math.random().toString(36).slice(2), date: '', time: '19:00', opponent: '', isHome: 1, venue: '', timeTba: '' };
  }

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  // Generate recurring pattern rows
  const generateRecurring = () => {
    if (!recurStartDate || !recurWeeks) return;
    const start = new Date(recurStartDate);
    const targetDay = parseInt(recurDay);
    // Advance to first occurrence of target day
    while (start.getDay() !== targetDay) start.setDate(start.getDate() + 1);

    const newRows = [];
    for (let w = 0; w < recurWeeks; w++) {
      const d = new Date(start);
      d.setDate(d.getDate() + w * 7);
      newRows.push({
        id: Math.random().toString(36).slice(2),
        date: d.toISOString().split('T')[0],
        time: recurTime,
        opponent: '',
        isHome: recurHome ? (w % 2 === 0 ? 1 : 0) : (w % 2 === 0 ? 0 : 1),
        venue: '',
        timeTba: '',
      });
    }
    setRows(newRows);
    setMode('manual'); // Switch to manual to let user fill in opponents
  };

  // Copy from prior schedule
  const copyFromSchedule = async () => {
    if (!copyFromSlug) return;
    const sched = schedules.find(s => s.slug === copyFromSlug);
    if (!sched) return;
    try {
      const data = await bridgeApi.get(`/schedules/${sched.id}/games`, orgId);
      const games = data.data || [];
      if (games.length === 0) { alert('No games found in that schedule.'); return; }

      const newRows = games.map(g => {
        // Read the floating-local epoch as literal UTC digits (offset 0).
        const dtg = g.unix_dtg ? new Date(g.unix_dtg * 1000) : null;
        const pad = (n) => String(n).padStart(2, '0');
        return {
          id: Math.random().toString(36).slice(2),
          date: dtg ? `${dtg.getUTCFullYear()}-${pad(dtg.getUTCMonth() + 1)}-${pad(dtg.getUTCDate())}` : '',
          time: dtg ? `${pad(dtg.getUTCHours())}:${pad(dtg.getUTCMinutes())}` : '19:00',
          opponent: g.opponent || g.title || '',
          isHome: g.is_home_game || 0,
          venue: '',
          timeTba: g.time_tba || '',
        };
      });
      setRows(newRows);
      setMode('manual');
    } catch (e) {
      alert(`Failed to load schedule: ${e.message}`);
    }
  };

  // Save all rows as games
  const handleSaveAll = async () => {
    if (!scheduleSlug) { alert('Please select a schedule first.'); return; }
    const validRows = rows.filter(r => r.date);
    if (validRows.length === 0) { alert('No valid games to save (need at least a date).'); return; }

    setSaving(true);
    setSaved(0);
    setErrors([]);
    const newErrors = [];

    for (let i = 0; i < validRows.length; i++) {
      const r = validRows[i];
      try {
        let unix_dtg = 0;
        if (r.date) {
          // Floating-local: encode the entered wall-clock at UTC offset 0
          // (browser-independent). See /app/docs/OL_TIMEZONE.md §5A.
          const dm = String(r.date).match(/^(\d{4})-(\d{2})-(\d{2})$/);
          const tm = String(r.time || '19:00').match(/^(\d{1,2}):(\d{2})/);
          if (dm) {
            unix_dtg = Math.floor(Date.UTC(+dm[1], +dm[2] - 1, +dm[3], tm ? +tm[1] : 19, tm ? +tm[2] : 0) / 1000);
          }
        }
        await bridgeApi.post('/games', orgId, {
          title: r.opponent ? `${r.isHome ? 'vs' : 'at'} ${r.opponent}` : '',
          schedule_slug: scheduleSlug,
          unix_dtg,
          time_tba: r.timeTba || '',
          opponent: r.opponent,
          is_home_game: r.isHome,
          location: r.venue,
        });
        setSaved(prev => prev + 1);
      } catch (e) {
        newErrors.push({ row: i + 1, error: e.response?.data?.detail || e.message });
      }
    }
    setErrors(newErrors);
    setSaving(false);
    if (newErrors.length === 0) onComplete?.();
  };

  const selectedSched = schedules.find(s => s.slug === scheduleSlug);

  return (
    <div className="space-y-4 max-w-4xl" data-testid="schedule-builder">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-5 w-5 text-slate-400" /></Button>
        <div>
          <h2 className="text-lg font-bold text-slate-100">Schedule Builder</h2>
          <p className="text-xs text-slate-400">Bulk-create games for a schedule</p>
        </div>
      </div>

      {/* Target Schedule */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardContent className="p-4 space-y-3">
          <Label className="text-slate-300 text-xs">Target Schedule *</Label>
          <Select value={scheduleSlug || '__none__'} onValueChange={v => setScheduleSlug(v === '__none__' ? '' : v)}>
            <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200" data-testid="builder-schedule-select"><SelectValue placeholder="Select schedule" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select a schedule...</SelectItem>
              {schedules.filter(s => s.slug).map(s => <SelectItem key={s.slug || s.id} value={s.slug}>{s.title} ({s.slug})</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Mode Selector */}
      <div className="flex gap-2">
        {[{ k: 'manual', l: 'Manual Entry', icon: Plus }, { k: 'recurring', l: 'Recurring Pattern', icon: Calendar }, { k: 'copy', l: 'Copy Prior Season', icon: Copy }].map(m => (
          <Button key={m.k} variant={mode === m.k ? 'default' : 'outline'} size="sm" onClick={() => setMode(m.k)}
            className={mode === m.k ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-600 text-slate-300'} data-testid={`builder-mode-${m.k}`}>
            <m.icon className="h-3.5 w-3.5 mr-1" />{m.l}
          </Button>
        ))}
      </div>

      {/* Recurring Pattern */}
      {mode === 'recurring' && (
        <Card className="bg-slate-800/40 border-slate-700/50">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Day of Week</Label>
                <Select value={recurDay} onValueChange={setRecurDay}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d,i) => (
                      <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Time</Label>
                <Input type="time" value={recurTime} onChange={e => setRecurTime(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200 h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Start Date</Label>
                <Input type="date" value={recurStartDate} onChange={e => setRecurStartDate(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200 h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Weeks</Label>
                <Input type="number" value={recurWeeks} onChange={e => setRecurWeeks(parseInt(e.target.value) || 0)} className="bg-slate-900 border-slate-700 text-slate-200 h-9" />
              </div>
            </div>
            <Button onClick={generateRecurring} disabled={!recurStartDate} className="bg-amber-600 hover:bg-amber-700" size="sm" data-testid="generate-recurring-btn">
              <Calendar className="h-3.5 w-3.5 mr-1" />Generate {recurWeeks} Games
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Copy Prior Season */}
      {mode === 'copy' && (
        <Card className="bg-slate-800/40 border-slate-700/50">
          <CardContent className="p-4 space-y-3">
            <Label className="text-slate-300 text-xs">Copy games from schedule</Label>
            <div className="flex gap-2">
              <Select value={copyFromSlug || '__none__'} onValueChange={v => setCopyFromSlug(v === '__none__' ? '' : v)}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 flex-1"><SelectValue placeholder="Select source schedule" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select...</SelectItem>
                  {schedules.filter(s => s.slug && s.slug !== scheduleSlug).map(s => (
                    <SelectItem key={s.slug || s.id} value={s.slug}>{s.title} ({s.slug})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={copyFromSchedule} disabled={!copyFromSlug} className="bg-amber-600 hover:bg-amber-700" size="sm" data-testid="copy-schedule-btn">
                <Copy className="h-3.5 w-3.5 mr-1" />Copy
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Game Rows */}
      <div className="space-y-1">
        <div className="grid grid-cols-[120px_80px_1fr_80px_1fr_60px_40px] gap-2 px-2 text-[10px] text-slate-500 uppercase font-medium">
          <span>Date</span><span>Time</span><span>Opponent</span><span>H/A</span><span>Venue/Location</span><span>TBA</span><span></span>
        </div>
        {rows.map((row, i) => (
          <div key={row.id} className="grid grid-cols-[120px_80px_1fr_80px_1fr_60px_40px] gap-2 items-center" data-testid={`builder-row-${i}`}>
            <Input type="date" value={row.date} onChange={e => updateRow(i, 'date', e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs" />
            <Input type="time" value={row.time} onChange={e => updateRow(i, 'time', e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs" />
            <Input value={row.opponent} onChange={e => updateRow(i, 'opponent', e.target.value)} placeholder="Opponent name" className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs" />
            <button onClick={() => updateRow(i, 'isHome', row.isHome ? 0 : 1)} className={`h-8 rounded text-xs font-medium px-2 ${row.isHome ? 'bg-emerald-600/30 text-emerald-400' : 'bg-blue-600/30 text-blue-400'}`}>
              {row.isHome ? 'Home' : 'Away'}
            </button>
            <Input value={row.venue} onChange={e => updateRow(i, 'venue', e.target.value)} placeholder="Location" className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs" />
            <button onClick={() => updateRow(i, 'timeTba', row.timeTba ? '' : 'TBA')} className={`h-8 rounded text-[10px] px-1 ${row.timeTba ? 'bg-amber-600/30 text-amber-400' : 'bg-slate-700/30 text-slate-500'}`}>
              {row.timeTba || '—'}
            </button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-red-400" onClick={() => removeRow(i)}><Trash2 className="h-3 w-3" /></Button>
          </div>
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={addRow} className="border-slate-600 text-slate-300" data-testid="add-row-btn">
        <Plus className="h-3.5 w-3.5 mr-1" />Add Row
      </Button>

      {/* Status / Errors */}
      {saving && (
        <div className="flex items-center gap-2 text-sm text-amber-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Saving {saved}/{rows.filter(r => r.date).length} games...
        </div>
      )}
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((e, i) => (
            <div key={i} className="text-xs text-red-400 bg-red-900/20 p-1.5 rounded">Row {e.row}: {e.error}</div>
          ))}
        </div>
      )}

      {/* Save All */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onBack} className="border-slate-600 text-slate-300">Cancel</Button>
        <Button onClick={handleSaveAll} disabled={!scheduleSlug || saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-all-games-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
          Save {rows.filter(r => r.date).length} Games
        </Button>
      </div>
    </div>
  );
}
